/**
 * Inbox "Recent tasks" API client.
 *
 * Wraps the additive gateway endpoint:
 *   GET /api/orgs/:orgId/inbox/recent-cards?cursor=<opaque>&limit=<N>
 *        → { items: RecentCard[], nextCursor: string | null }
 *
 * Keyset-paginated by (recency, id) DESC where recency = GREATEST(updated_at,
 * created_at). `nextCursor` is an opaque token; pass it back verbatim to fetch
 * the next (older) page. `null` ⇒ end of the list. Read-only, no flag — the
 * component owns when to call it.
 */
import { apiJson } from './client';

export interface RecentCard {
  cardId: string;
  title: string | null;
  /** Column kind — coarse status (backlog|todo|in_progress|for_approval|done|archive|formulating|custom). */
  status: string | null;
  /** Human column label, e.g. "In progress". */
  columnName: string | null;
  boardId: string | null;
  projectId: string | null;
  projectName: string | null;
  boardName: string | null;
  orgId: string;
  /** ISO recency = GREATEST(updated_at, created_at). */
  recencyAt: string;
}

export interface RecentCardsPage {
  items: RecentCard[];
  nextCursor: string | null;
}

export const recentCardsApi = {
  /** One keyset page of recently-touched cards for the org (newest first). */
  list(orgId: string, cursor?: string | null, limit = 30): Promise<RecentCardsPage> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return apiJson<RecentCardsPage>(`/orgs/${orgId}/inbox/recent-cards?${qs.toString()}`);
  },
  /**
   * GLOBAL (cross-org) one keyset page — recently-touched cards across ALL the
   * user's orgs (founder 2026-07-05). Same shape + opaque cursor as `list`; the
   * gateway resolves the org set (system founder → all orgs; member → their
   * memberships) and threads the sub-scope fence per org. Each item carries its
   * own `orgId` so the UI can badge which org it belongs to.
   */
  listGlobal(cursor?: string | null, limit = 30): Promise<RecentCardsPage> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return apiJson<RecentCardsPage>(`/inbox/recent-cards?${qs.toString()}`);
  },
};

export const recentCardsKeys = {
  all: ['inbox', 'recent-cards'] as const,
  list: (orgId: string) => [...recentCardsKeys.all, orgId] as const,
  /** GLOBAL cross-org list key (no org id). */
  global: () => [...recentCardsKeys.all, 'global'] as const,
};
